import { useEffect, useMemo, useState } from "react";

import { resolvePublicEntity } from "../api/auth";

const normalizeType = (type) => {
  if (type === "AGENCY") return "AGENCY_OWNER";
  if (type === "AGENT") return "AGENCY_AGENT";
  return String(type || "").toUpperCase();
};

const entityKey = (type, id) => {
  const normalizedType = normalizeType(type);
  const normalizedId = id ? String(id) : "";
  return normalizedType && normalizedId
    ? `${normalizedType}:${normalizedId}`
    : null;
};

export const makePublicEntity = (type, id) => {
  const key = entityKey(type, id);
  return key
    ? { type: normalizeType(type), id: String(id), key }
    : null;
};

export function usePublicEntities(entities) {
  const uniqueEntities = useMemo(() => {
    const result = new Map();
    (entities || []).forEach((entity) => {
      const normalized = makePublicEntity(entity?.type, entity?.id);
      if (normalized) result.set(normalized.key, normalized);
    });
    return Array.from(result.values());
  }, [entities]);
  const [profiles, setProfiles] = useState({});

  useEffect(() => {
    let cancelled = false;
    const missing = uniqueEntities.filter(
      (entity) => !Object.prototype.hasOwnProperty.call(profiles, entity.key)
    );
    if (!missing.length) return undefined;

    Promise.all(
      missing.map(async (entity) => {
        try {
          const response = await resolvePublicEntity(entity);
          return [entity.key, response.data];
        } catch {
          return [entity.key, null];
        }
      })
    ).then((entries) => {
      if (!cancelled) {
        setProfiles((previous) => ({
          ...previous,
          ...Object.fromEntries(entries),
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [profiles, uniqueEntities]);

  const getEntity = (type, id) => {
    const key = entityKey(type, id);
    return key ? profiles[key] : null;
  };

  return { getEntity };
}
